import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { CourseStructure, MainContent, ToolsPanel } from '../components/layout';
import { LoadingSpinner } from '../components/ui';
import { useLocalStorage } from '../hooks';
import { topicService } from '../services/topicService';
import { useAudioStore, useAuthStore } from '../stores';
import '../styles/MainContent.css';

const AUDIO_BASE_URL = "https://somayyaacademy.s3.ap-south-1.amazonaws.com/somayyaAcademyEdTech/AUDIO/";

const ReadingPage = ({ courseData: propCourseData, pdfData: propPdfData }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [textSize, setTextSize] = useLocalStorage('textSize', 'normal');
  const [isLoading, setIsLoading] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [error, setError] = useState(null);
  const [courseData, setCourseData] = useState(propCourseData || null);
  const [currentTopic, setCurrentTopic] = useState(null);
  const [activeTopicId, setActiveTopicId] = useState(null);
  const audioRef = useRef(null);
  const [syncCallback, setSyncCallback] = useState(null);
  const [targetHighlightId, setTargetHighlightId] = useState(null);

  // Audio store
  const { initialize: initializeAudio, loadPagePlaylist } = useAudioStore();
  const logout = useAuthStore((state) => state.logout);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login', { replace: true });
    } catch (logoutError) {
      console.error('Reading page logout failed:', logoutError);
      toast.error(logoutError.message || 'Logout failed. Please try again.');
    }
  };

  // Handle panel close with sync callback
  const handlePanelClose = useCallback((callback) => {
    if (callback && typeof callback === 'function') {
      callback();
    }
  }, []);

  // Handle sync callback from MainContent
  const handleSyncCallback = useCallback((callback) => {
    setSyncCallback(() => callback);
  }, []);

  // Handle scroll complete
  const handleScrollComplete = useCallback(() => {
    setTargetHighlightId(null);
  }, []);

  // Load data from props, location state, or session storage
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        let data = null;

        // Priority: props > location state > session storage
        if (propCourseData && propPdfData) {
          data = {
            courseData: propCourseData,
            pdfData: propPdfData
          };
        } else if (location.state?.courseData && location.state?.topic) {
          // Coming from CourseDetailsPage with full course data
          data = {
            courseData: location.state.courseData,
            pdfData: location.state.topic
          };
        } else if (location.state?.topic && location.state?.courseId) {
          // Fallback: Coming from CourseDetailsPage with minimal data
          data = {
            courseData: {
              id: location.state.courseId,
              title: 'Course Title', // You might want to fetch this
              topics: [location.state.topic]
            },
            pdfData: location.state.topic
          };
        } else {
          // Try to load from session storage (from TopicsPage)
          const sessionData = sessionStorage.getItem('readingPageData');
          if (sessionData) {
            data = JSON.parse(sessionData);
          }
        }

        if (!data || !data.pdfData) {
          setError('No PDF data available');
          return;
        }

        // Process the courseData to ensure it has the right structure
        const processedCourseData = {
          id: data.courseData?.id || 'single-pdf',
          title: data.courseData?.title || data.pdfData.title || 'Course',
          description: data.courseData?.description || '',
          displayPicture: data.courseData?.displayPicture || null,
          // Preserve all original course topics, not just the current one
          topics: data.courseData?.topics || [data.pdfData]
        };

        // Ensure topics have proper structure
        const processedTopics = processedCourseData.topics.map((topic, index) => ({
          ...topic,
          id: topic.id || `topic-${index}`,
          title: topic.title || `Topic ${index + 1}`,
          content: topic.content || {
            heading: topic.title || `Topic ${index + 1}`,
            body: topic.body || []
          },
          currentPageIndex: topic.currentPageIndex || 0,
          // Preserve resources from API response
          resources: topic.resources || [],
          // If course data has isPurchase: true, inherit it to all topics
          isPurchase: data.courseData?.isPurchase === true ? true : topic.isPurchase,
          originalStructure: topic.originalStructure || {
            pageId: `page-0`,
            totalPages: topic.resources?.length || 1,
            topicId: topic.id || `topic-${index}`,
            subTopicId: topic.subTopicId || 'default'
          }
        }));

        const finalCourseData = {
          ...processedCourseData,
          topics: processedTopics
        };

        setCourseData(finalCourseData);

        // Find the selected topic (if coming from CourseDetailsPage) or use the first one
        let selectedTopic = processedTopics[0];
        if (location.state?.topic) {
          const foundTopic = processedTopics.find(t => t.id === location.state.topic.id);
          if (foundTopic) {
            selectedTopic = foundTopic;
          }
        } else if (data.pdfData && processedTopics.length > 1) {
          // If we have multiple topics and pdfData, find the matching topic
          const foundTopic = processedTopics.find(t => t.id === data.pdfData.id);
          if (foundTopic) {
            selectedTopic = foundTopic;
          }
        }

        if (selectedTopic) {
          setActiveTopicId(selectedTopic.id);

          // Fetch fresh API data for the selected topic to ensure we have resources
          try {
            const response = await topicService.getTopicById(selectedTopic.id);

            if (response && response.data) {
              const topicData = response.data.data;

              // Process resources as pages - filter only HTML resources
              const resources = topicData.resources || [];
              const htmlResources = resources.filter(resource => resource.resourceTypeName === 'HTML');
              const sortedResources = htmlResources.sort((a, b) => (a.orderIndex || 0) - (b.orderIndex || 0));

              // Create a topic object with resources as pages
              const processedTopic = {
                ...topicData,
                id: topicData.id,
                title: topicData.title,
                currentPageIndex: 0,
                resources: sortedResources,
                originalStructure: {
                  pageId: 'page-0',
                  totalPages: sortedResources.length,
                  topicId: topicData.id,
                  subTopicId: 'default'
                },
                realTopicId: topicData.id
              };

              setCurrentTopic(processedTopic);

              // Update the courseData to reflect the correct page count for this topic
              setCourseData(prevCourseData => {
                if (!prevCourseData) return prevCourseData;

                const updatedTopics = prevCourseData.topics.map(topic => {
                  if (topic.id === selectedTopic.id) {
                    return {
                      ...topic,
                      resources: sortedResources,
                      originalStructure: {
                        ...topic.originalStructure,
                        totalPages: sortedResources.length
                      }
                    };
                  }
                  return topic;
                });

                return {
                  ...prevCourseData,
                  topics: updatedTopics
                };
              });

              // Initialize audio if available
              let audioSources = topicData.audioSources || [];
              if (audioSources.length === 0 && topicData.resources) {
                audioSources = topicData.resources
                  .filter(r => r.resourceTypeName === 'Audio' && r.url)
                  .map(r => r.url.startsWith('http') ? r.url : `${AUDIO_BASE_URL}${r.url}`);
              }

              if (audioSources.length > 0) {
                loadPagePlaylist(topicData.id, audioSources);
              }
            } else {
              // Fallback to original topic if API fails
              setCurrentTopic(selectedTopic);
            }
          } catch (error) {
            console.error('Error fetching initial topic data:', error);
            // Fallback to original topic if API fails
            setCurrentTopic(selectedTopic);
          }
        }
      } catch (err) {
        console.error('Error loading reading page data:', err);
        setError('Failed to load content');
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [propCourseData, propPdfData, location.state, loadPagePlaylist]);

  // Initialize audio store
  useEffect(() => {
    const cleanup = initializeAudio(audioRef);
    return cleanup;
  }, [initializeAudio]);

  // Handle topic selection
  const handleSelectTopic = useCallback(async (topicId) => {
    if (!courseData) return;

    setIsTransitioning(true);
    try {
      // Fetch topic data from API
      const response = await topicService.getTopicById(topicId);

      if (response && response.data) {
        const topicData = response.data.data; // Extract nested data from API response

        // Process resources as pages - filter only HTML resources
        const resources = topicData.resources || [];
        const htmlResources = resources.filter(resource => resource.resourceTypeName === 'HTML');
        const sortedResources = htmlResources.sort((a, b) => (a.orderIndex || 0) - (b.orderIndex || 0));

        // Create a topic object with resources as pages
        const processedTopic = {
          ...topicData,
          id: topicData.id,
          title: topicData.title,
          currentPageIndex: 0, // Start with first page
          resources: sortedResources,
          // Keep original structure for backward compatibility
          originalStructure: {
            pageId: 'page-0',
            totalPages: sortedResources.length,
            topicId: topicData.id,
            subTopicId: 'default'
          },
          // Add realTopicId for consistency
          realTopicId: topicData.id
        };

        console.log('🎯 Processed topic with resources:', processedTopic);
        console.log('📄 Number of pages/resources:', sortedResources.length);

        setCurrentTopic(processedTopic);
        setActiveTopicId(topicId);

        // Update the courseData to reflect the correct page count for this topic
        setCourseData(prevCourseData => {
          if (!prevCourseData) return prevCourseData;

          const updatedTopics = prevCourseData.topics.map(topic => {
            if (topic.id === topicId) {
              return {
                ...topic,
                resources: sortedResources,
                originalStructure: {
                  ...topic.originalStructure,
                  totalPages: sortedResources.length
                }
              };
            }
            return topic;
          });

          return {
            ...prevCourseData,
            topics: updatedTopics
          };
        });

        // Load audio playlist for the new topic if available
        let audioSources = topicData.audioSources || [];
        if (audioSources.length === 0 && topicData.resources) {
          audioSources = topicData.resources
            .filter(r => r.resourceTypeName === 'Audio' && r.url)
            .map(r => r.url.startsWith('http') ? r.url : `${AUDIO_BASE_URL}${r.url}`);
        }

        if (audioSources.length > 0) {
          loadPagePlaylist(topicData.id, audioSources);
        }

        return processedTopic;
      }
    } catch (error) {
      console.error('Error fetching topic:', error);
      setError('Failed to load topic content');
    } finally {
      setIsTransitioning(false);
    }
  }, [courseData, loadPagePlaylist, setError]);

  // Handle page navigation within a topic
  const handlePageChange = useCallback((topicId, pageNumber) => {
    if (!currentTopic) return;

    // Check if the topic ID matches the current topic
    if (currentTopic.id === topicId) {
      // Update the topic with the new page index
      const updatedTopic = {
        ...currentTopic,
        currentPageIndex: pageNumber - 1 // Convert from 1-indexed to 0-indexed
      };

      // If the topic has originalStructure, update it as well
      if (updatedTopic.originalStructure) {
        updatedTopic.originalStructure = {
          ...updatedTopic.originalStructure,
          pageNumber: pageNumber
        };
      }

      // Update the current topic
      setCurrentTopic(updatedTopic);

      // Ensure activeTopicId remains set to maintain the active state in CourseStructure
      setActiveTopicId(topicId);

      // If we have courseData, update the topic there as well
      if (courseData) {
        // Update the topic in the course data - PRESERVE ALL ORIGINAL PROPERTIES
        const updatedTopics = courseData.topics.map(t =>
          t.id === topicId ? {
            ...t, // Preserve all original properties including isPurchase
            currentPageIndex: pageNumber - 1,
            originalStructure: t.originalStructure ? {
              ...t.originalStructure,
              pageNumber: pageNumber
            } : undefined
          } : t
        );

        setCourseData(prevData => ({
          ...prevData, // Preserve all courseData properties
          topics: updatedTopics
        }));
      }
    }
  }, [currentTopic, courseData]);

  // Handle navigation to specific page (from bookmarks)
  const handleNavigateToPage = useCallback(async (topicId, pageId, highlight = null) => {
    if (!courseData) return;

    console.log(`🚀 handleNavigateToPage called - topicId: ${topicId}, pageId: ${pageId}, highlight:`, highlight);

    setIsTransitioning(true);

    // Clear any pending highlight scroll
    setTargetHighlightId(null);

    try {
      // Always fetch the topic to ensure we have fresh data and resources
      const response = await topicService.getTopicById(topicId);

      if (response && response.data) {
        const topicData = response.data.data;
        const existingTopic = courseData.topics.find(t => t.id === topicId);

        // Process resources as pages - filter only HTML resources
        const resources = topicData.resources || [];
        const htmlResources = resources.filter(resource => resource.resourceTypeName === 'HTML');
        const sortedResources = htmlResources.sort((a, b) => (a.orderIndex || 0) - (b.orderIndex || 0));

        // Resolve Page Index
        let pageIndex = 0;
        if (pageId !== null && pageId !== undefined) {
          if (typeof pageId === 'string') {
            // Check for Robust Page ID format first (e.g., "...__page1") - 1-based
            const robustMatch = pageId.match(/__page(\d+)$/);
            if (robustMatch) {
              pageIndex = parseInt(robustMatch[1], 10) - 1; // Convert 1-based to 0-based
              console.log(`Parsed Robust Page ID: ${pageId} -> Index: ${pageIndex}`);
            } else {
              // Check for legacy/simple format "page-0" - 0-based
              const pageMatch = pageId.match(/page-(\d+)/);
              if (pageMatch) {
                pageIndex = parseInt(pageMatch[1], 10);
                console.log(`Parsed Legacy Page ID: ${pageId} -> Index: ${pageIndex}`);
              } else {
                // Assume it's a resource ID, try to find it in the sorted resources
                const resourceIndex = sortedResources.findIndex(r => r.id === pageId);
                if (resourceIndex !== -1) {
                  pageIndex = resourceIndex;
                  console.log(`Resolved Resource ID: ${pageId} -> Index: ${pageIndex}`);
                } else {
                  // Try parsing as number if it's just a string number
                  const parsed = parseInt(pageId, 10);
                  if (!isNaN(parsed)) {
                    pageIndex = parsed;
                    console.log(`Parsed Numeric String: ${pageId} -> Index: ${pageIndex}`);
                  }
                }
              }
            }
          } else if (typeof pageId === 'number') {
            pageIndex = pageId;
          }
        }

        // Validate Page Index
        if (pageIndex < 0) pageIndex = 0;
        if (sortedResources.length > 0 && pageIndex >= sortedResources.length) {
          console.warn(`⚠️ Page index ${pageIndex} out of bounds (total: ${sortedResources.length}). Resetting to 0.`);
          pageIndex = 0;
        }

        console.log(`📍 Navigating to Topic: ${topicId}, Page Index: ${pageIndex}`);

        // Create a topic object with resources as pages
        const processedTopic = {
          ...topicData,
          id: topicData.id,
          title: topicData.title,
          currentPageIndex: pageIndex,
          resources: sortedResources,
          // Preserve isPurchase from existing topic if available
          isPurchase: existingTopic?.isPurchase ?? topicData.isPurchase,
          // Keep original structure for backward compatibility
          originalStructure: {
            pageId: `page-${pageIndex}`,
            totalPages: sortedResources.length,
            topicId: topicData.id,
            subTopicId: 'default',
            pageNumber: pageIndex + 1
          },
          // Add realTopicId for consistency
          realTopicId: topicData.id
        };

        setCurrentTopic(processedTopic);
        setActiveTopicId(topicId);

        // Update the courseData to reflect the correct page count and current index
        setCourseData(prevCourseData => {
          if (!prevCourseData) return prevCourseData;

          const updatedTopics = prevCourseData.topics.map(topic => {
            if (topic.id === topicId) {
              return {
                ...topic,
                ...processedTopic, // Update with fresh data
                resources: sortedResources,
                currentPageIndex: pageIndex,
                originalStructure: {
                  ...topic.originalStructure,
                  totalPages: sortedResources.length,
                  pageNumber: pageIndex + 1
                }
              };
            }
            return topic;
          });

          return {
            ...prevCourseData,
            topics: updatedTopics
          };
        });

        // Load audio playlist for the new topic if available
        let audioSources = topicData.audioSources || [];
        if (audioSources.length === 0 && topicData.resources) {
          audioSources = topicData.resources
            .filter(r => r.resourceTypeName === 'Audio' && r.url)
            .map(r => r.url.startsWith('http') ? r.url : `${AUDIO_BASE_URL}${r.url}`);
        }

        if (audioSources.length > 0) {
          loadPagePlaylist(topicData.id, audioSources);
        }

        // Handle highlight scrolling
        if (highlight && highlight.id) {
          console.log(`📌 Setting target highlight: ${highlight.id}`);
          setTargetHighlightId(highlight.id);
        }
      }
    } catch (error) {
      console.error('Error navigating to page:', error);
      setError('Failed to load content');
    } finally {
      setIsTransitioning(false);
    }
  }, [courseData, loadPagePlaylist, setError]);

  // Handle text size change
  const handleTextSizeChange = useCallback((size) => {
    setTextSize(size);
  }, [setTextSize]);

  // Handle back navigation
  const handleBack = useCallback(() => {
    // Clear session storage
    sessionStorage.removeItem('readingPageData');

    // Navigate back based on how we got here
    if (location.state?.courseId) {
      navigate(`/course/${location.state.courseId}`);
    } else {
      navigate('/dashboard');
    }
  }, [navigate, location.state]);

  const handleBackToCourses = useCallback(() => {
    sessionStorage.removeItem('readingPageData');
    navigate('/dashboard');
  }, [navigate]);

  // Loading state
  if (isLoading) {
    return (
      <div className="app-container">
        <div className="loading-container">
          <LoadingSpinner size="large" message="Loading PDF..." />
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="app-container">
        <div className="error-container">
          <h2>{error}</h2>
          <button onClick={handleBack} className="back-button">
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // No data state
  if (!courseData || !currentTopic) {
    return (
      <div className="app-container">
        <div className="error-container">
          <h2>No content available</h2>
          <button onClick={handleBack} className="back-button">
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="app-container">
      {/* Course Structure Sidebar */}
      <CourseStructure
        topics={courseData.topics}
        activeTopicId={activeTopicId}
        onSelectTopic={handleSelectTopic}
        courseData={courseData}
        onNavigateToPage={handleNavigateToPage}
        onPanelClose={handlePanelClose}
        syncCallback={syncCallback}
        onLogout={handleLogout}
        onBackToCourses={handleBackToCourses}
      />

      {/* Main Content Area with transition effect */}
      <div className={`main-content-wrapper ${isTransitioning ? 'transitioning' : ''}`}>
        {isTransitioning && (
          <div className="transition-indicator">
            <div className="transition-spinner"></div>
          </div>
        )}
        <MainContent
          topic={currentTopic}
          textSize={textSize}
          courseData={courseData}
          onPanelClose={handleSyncCallback}
          targetHighlightId={targetHighlightId}
          onScrollComplete={handleScrollComplete}
        />
      </div>

      {/* Tools Panel Sidebar */}
      <ToolsPanel
        activeTextSize={textSize}
        onSetTextSize={handleTextSizeChange}
        currentTopic={currentTopic}
        onPageChange={handlePageChange}
        courseData={courseData}
      />

      {/* Hidden Audio Element for Audio Store */}
      <audio
        ref={audioRef}
        id="global-audio-player"
        style={{ display: 'none' }}
        preload="metadata"
      />
    </div>
  );
};

export default ReadingPage;